import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "react-query";
interface IntegrationOAuthResultMessage {
  type: string;
  success?: boolean;
  error?: string | null;
  /** True when the callback page chained the next OAuth leg server-side. */
  chained?: boolean;
  [key: string]: unknown;
}
export interface IntegrationOAuthChainOptions {
  /**
   * Returns the URL of the chained (second) OAuth leg, called at the moment
   * the first leg succeeds. Return null/undefined to skip chaining — e.g.
   * the user's account is already linked, or the link URL hasn't loaded.
   */
  getChainUrl: () => string | null | undefined;
  /**
   * Query keys to invalidate when the chained leg delivers a result or its
   * popup closes.
   */
  queryKeysToInvalidate: string[][];
  /** Fires once the popup has been navigated to the chained URL. */
  onStart?: () => void;
  onSuccess?: (message: IntegrationOAuthResultMessage) => void;
  onError?: (reason: string | null, message: IntegrationOAuthResultMessage) => void;
  /**
   * Fires when the popup closes during the chained leg without delivering a
   * result message (e.g. the user dismisses the second OAuth screen).
   */
  onClose?: () => void;
}
interface UseIntegrationOAuthPopupOptions {
  /** Unique window name so multiple hook instances don't share a popup */
  popupName: string;
  /** postMessage `type` emitted by the OAuth callback HTML */
  resultType: string;
  queryKeysToInvalidate: string[][];
  onSuccess?: (message: IntegrationOAuthResultMessage) => void;
  onError?: (reason: string | null, message: IntegrationOAuthResultMessage) => void;
  /**
   * Fires when the popup/tab is closed without delivering a result message
   * (e.g. the user dismisses the OAuth window). Lets callers reset any
   * "connecting" UI state that `onSuccess`/`onError` would otherwise own.
   */
  onClose?: () => void;
  /**
   * Optional second OAuth leg chained into the same popup window (e.g.
   * user-account linking immediately after a workspace install). Preferred
   * path: the backend's callback page redirects the popup to the second leg
   * itself and marks its result message `chained: true`, which works even
   * when the provider's Cross-Origin-Opener-Policy severed `window.opener`
   * and no message ever reaches us. When the first leg succeeds via
   * postMessage without the server-side chain, the still-open popup — the
   * callback page only closes itself after a short delay — is navigated
   * straight to `getChainUrl()`. Navigating an existing window is not
   * subject to the popup blocker (unlike `window.open` from a message
   * handler, which has no user activation) and discards the callback page's
   * pending `window.close()` timer. Either way, the next result message is
   * routed to the chain callbacks instead of the first leg's. If the chain
   * can't start (popup already closed, result arrived via close-polling, or
   * no chain URL available), callers should fall back to a click-driven
   * flow.
   */
  chain?: IntegrationOAuthChainOptions;
}

// Origin of the backend API — the OAuth callback HTML is served from this domain
const API_ORIGIN = process.env.NEXT_PUBLIC_API_URL ? new URL(process.env.NEXT_PUBLIC_API_URL).origin : "";
export function useIntegrationOAuthPopup({
  popupName,
  resultType,
  queryKeysToInvalidate,
  onSuccess,
  onError,
  onClose,
  chain
}: UseIntegrationOAuthPopupOptions) {
  const queryClient = useQueryClient();
  const popupRef = useRef<Window | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const focusListenerRef = useRef<(() => void) | null>(null);
  const queryKeysRef = useRef(queryKeysToInvalidate);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onCloseRef = useRef(onClose);
  const chainRef = useRef(chain);
  // True while the popup is on the chained (second) OAuth leg.
  const chainActiveRef = useRef(false);
  queryKeysRef.current = queryKeysToInvalidate;
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;
  onCloseRef.current = onClose;
  chainRef.current = chain;
  const invalidateQueries = useCallback(() => {
    for (const key of queryKeysRef.current) {
      queryClient.invalidateQueries(key);
    }
    if (chainActiveRef.current) {
      for (const key_0 of chainRef.current?.queryKeysToInvalidate ?? []) {
        queryClient.invalidateQueries(key_0);
      }
    }
  }, [queryClient]);
  const invalidateChainQueries = useCallback(() => {
    for (const key_1 of chainRef.current?.queryKeysToInvalidate ?? []) {
      queryClient.invalidateQueries(key_1);
    }
  }, [queryClient]);
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (focusListenerRef.current) {
      window.removeEventListener("focus", focusListenerRef.current);
      focusListenerRef.current = null;
    }
  }, []);

  // Shared close handling for both polling paths. Routes to the chain's
  // callbacks when the popup closed mid-chained-leg.
  const handlePopupClosed = useCallback(() => {
    const wasChained = chainActiveRef.current;
    chainActiveRef.current = false;
    stopPolling();
    popupRef.current = null;
    if (wasChained) {
      invalidateChainQueries();
      chainRef.current?.onClose?.();
    } else {
      invalidateQueries();
      onCloseRef.current?.();
    }
  }, [invalidateChainQueries, invalidateQueries, stopPolling]);
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== API_ORIGIN) return;
      if (event.data?.type !== resultType) return;
      // Only handle messages from the popup this hook instance opened.
      if (!popupRef.current || event.source !== popupRef.current) return;
      const message = event.data as IntegrationOAuthResultMessage;
      if (chainActiveRef.current) {
        // Result of the chained (second) OAuth leg.
        chainActiveRef.current = false;
        popupRef.current = null;
        stopPolling();
        invalidateChainQueries();
        if (message.success) {
          chainRef.current?.onSuccess?.(message);
        } else {
          chainRef.current?.onError?.(message.error ?? null, message);
        }
        return;
      }
      invalidateQueries();

      // On first-leg success, continue the second OAuth leg in the same
      // popup. When the backend already chained it server-side (it
      // redirects the popup itself and marks the message `chained`), just
      // keep tracking the popup; otherwise try to navigate it before the
      // callback page's auto-close timer fires.
      let chained = false;
      if (message.success) {
        if (message.chained === true) {
          chained = true;
        } else if (chainRef.current) {
          const chainUrl = chainRef.current.getChainUrl();
          if (chainUrl && !popupRef.current.closed) {
            try {
              popupRef.current.location.href = chainUrl;
              chained = true;
            } catch {
              // Navigation can fail if the window is mid-close; fall back to
              // letting the popup close normally.
            }
          }
        }
      }
      if (chained) {
        // Keep the popup ref and close-polling alive for the chained leg.
        chainActiveRef.current = true;
        chainRef.current?.onStart?.();
      } else {
        popupRef.current = null;
        stopPolling();
      }
      if (message.success) {
        onSuccessRef.current?.(message);
      } else {
        onErrorRef.current?.(message.error ?? null, message);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [invalidateChainQueries, invalidateQueries, resultType, stopPolling]);
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);
  const openPopup = useCallback((url: string): boolean => {
    // Clean up any previous polling/listeners before opening a new popup
    stopPolling();
    chainActiveRef.current = false;
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const popup = window.open(url, popupName, `width=${width},height=${height},left=${left},top=${top},popup=yes`);
    if (!popup) {
      // Popup blocked — fall back to new tab with polling
      const tab = window.open(url, "_blank");
      if (!tab) {
        // Both popup and tab blocked
        return false;
      }
      popupRef.current = tab;

      // Start polling since postMessage won't work without window.opener
      pollingRef.current = setInterval(() => {
        invalidateQueries();
        if (popupRef.current?.closed) {
          handlePopupClosed();
        }
      }, 3000);
      const onFocus = () => invalidateQueries();
      focusListenerRef.current = onFocus;
      window.addEventListener("focus", onFocus);
      return true;
    }
    popupRef.current = popup;

    // Poll for popup close — covers both manual close and auto-close
    // after OAuth (postMessage may not be received cross-origin)
    pollingRef.current = setInterval(() => {
      if (popupRef.current?.closed) {
        handlePopupClosed();
      }
    }, 500);
    return true;
  }, [handlePopupClosed, invalidateQueries, popupName, stopPolling]);
  return {
    openPopup
  };
}